import type { EntityState} from "@@/shared/entity-status";
import { entityStateZodSchema } from "@@/shared/entity-status";
import type { StorageItemImageId } from "@@/shared/storage-items/storage-item-images/storage-item-image-id";
import type { Tags} from "@@/shared/tags";
import { tagsZodSchema } from "@@/shared/tags";
import type {
    RequiredTranslatable,
    Translatable} from "@@/shared/translations/translation";
import {
    requiredTranslatableZodSchema,
    translatableZodSchema,
} from "@@/shared/translations/translation";
import type { Except } from "type-fest";
import { z } from "zod";
import { storageItemImageIdZodSchema } from "../storage-items/storage-item-images/storage-item-image-id";
import type {
    ImageReferenceId} from "./image-reference-id";
import {
    imageReferenceIdFactory,
    imageReferenceIdZodSchema,
} from "./image-reference-id";

type ImageReferenceSettings = {
    [key: string]: string;
} & {
    _type: "IMAGE_REFERENCE_SETTINGS";
};
type ImageReferenceMeta = {
    readonly title: RequiredTranslatable;
    readonly description: Translatable;
    readonly tags: Tags;
};
const imageReferenceMetaZodSchema: z.ZodSchema<ImageReferenceMeta> = z.object({
    title: requiredTranslatableZodSchema,
    description: translatableZodSchema,
    tags: tagsZodSchema,
});

const imageReferenceZodObject = z.object({
    _id: imageReferenceIdZodSchema,
    _type: z.literal("IMAGE_REFERENCE"),
    _status: entityStateZodSchema,
    meta: imageReferenceMetaZodSchema,
    originalFilename: z.string().optional(),
    blurhash: z.string().optional(),
    path: z.string(),
    storageItemId: storageItemImageIdZodSchema.optional(),
});

type ImageReference = z.infer<typeof imageReferenceZodObject>;
type ImageReferenceWithRequiredStorageItemImageId = Except<
    ImageReference,
    "storageItemId"
> & {
    storageItemId: StorageItemImageId;
};
const imageReferenceZodSchema: z.ZodSchema<ImageReference> =
    imageReferenceZodObject;

type ImageReferenceFactoryParams = Except<
    ImageReference,
    "_id" | "_type" | "_status"
> & {
    _id?: ImageReferenceId;
    _status?: EntityState;
};
const imageReferenceFactory = (
    params: ImageReferenceFactoryParams,
): ImageReference => ({
    ...params,
    _id: params._id || imageReferenceIdFactory(),
    _type: "IMAGE_REFERENCE",
    _status: "ACTIVE",
});

export {
    imageReferenceFactory,
    imageReferenceZodObject,
    imageReferenceZodSchema,
};
export type {
    ImageReference,
    ImageReferenceMeta,
    ImageReferenceSettings,
    ImageReferenceWithRequiredStorageItemImageId,
};
